import { getExtensionState } from '@root/app/_utils/extension';
import { createContext, useCallback, useEffect, useMemo, useState } from 'react';
import { useLocalStorage } from 'react-use';
import poll from 'utils/poll';
import { trackEvent } from 'utils/tracking';

import { ExtensionConnectionHealth, ExtensionHealthState } from './types';

const EXTENSION_STATE_POLL_INTERVAL = 5000;
const DEFAULT_EXTENSION_STATE: ExtensionHealthState = {
  status: null,
  version: null,
  installed: false,
  connectionHealth: ExtensionConnectionHealth.INITIALIZING,
  options: null
};

export const ExtensionStateContext = createContext({
  extensionState: DEFAULT_EXTENSION_STATE,
  fetch: () => {}
});

type GetExtensionStateReturnTypeAwaited = Awaited<ReturnType<typeof getExtensionState>>;

/** This isn't consumed directly, rather look for `useExtensionState` in an adjacent file */
const useExtensionState = () => {
  const [extensionState, setExtensionState] = useState(DEFAULT_EXTENSION_STATE);
  const [connectionStatusLog, setConnectionStatusLog] = useState<{ state: GetExtensionStateReturnTypeAwaited; timestamp: number }[]>([]);
  const [didRecordUnreachableEvent, setDidRecordUnreachableEvent] = useState(false);
  const [scribeExtensionLocalStorage] = useLocalStorage<{
    id: string;
    version: string;
    lastHeard: number;
  }>('scribe_extension_state');

  const fetchStatus = useCallback(() => {
    return getExtensionState().then((response) => {
      setConnectionStatusLog((previous) => {
        const newLog = [
          {
            state: response,
            timestamp: Date.now()
          },
          ...previous
        ];

        return newLog.slice(0, 10);
      });
      return response;
    });
  }, []);

  // Fetch the extension status on mount and poll for updates
  useEffect(() => {
    fetchStatus();
    const pollUnsubscribe = poll(fetchStatus, EXTENSION_STATE_POLL_INTERVAL);

    return () => {
      pollUnsubscribe?.();
    };
  }, []);

  // Observe the connectionStatus log to create a new entry in healthLog
  useEffect(() => {
    if (connectionStatusLog.length > 0) {
      let newConnectionHealth = ExtensionConnectionHealth.INITIALIZING;

      if (connectionStatusLog.length >= 2 && connectionStatusLog[0].state?.installed && !connectionStatusLog[1].state?.installed) {
        // RECOVERING = if >=2 logs and the first 2 are defined, undefined
        newConnectionHealth = ExtensionConnectionHealth.RECOVERING;
      } else if (
        (connectionStatusLog.length === 1 && connectionStatusLog[0].state?.installed) ||
        (connectionStatusLog.length >= 2 && connectionStatusLog[0].state?.installed && connectionStatusLog[1].state?.installed)
      ) {
        // HEALTHY = (if 1 log entry and it's defined) or (if >=2 logs entries and first 2 are defined)
        newConnectionHealth = ExtensionConnectionHealth.HEALTHY;
      } else if (!connectionStatusLog[0].state?.installed) {
        // UNREACHABLE = (if 2 most recent logs are undefined)
        newConnectionHealth = ExtensionConnectionHealth.UNREACHABLE;
      }

      // If you've fired an event and now the extension is HEALTHY, fire event
      if (didRecordUnreachableEvent && newConnectionHealth === ExtensionConnectionHealth.HEALTHY) {
        trackEvent('frontend_extension_connection', {
          status: ExtensionConnectionHealth.HEALTHY,
          extension_version: scribeExtensionLocalStorage?.version
        });
        setDidRecordUnreachableEvent(false);
      }

      setExtensionState({
        ...connectionStatusLog[0].state,
        connectionHealth: newConnectionHealth
      });
    }
  }, [connectionStatusLog, scribeExtensionLocalStorage]);

  return useMemo(
    () => ({
      extensionState,
      fetch: fetchStatus
    }),
    [extensionState, fetchStatus]
  );
};

export const ExtensionStateProvider = ({ children }) => {
  const state = useExtensionState();

  return <ExtensionStateContext.Provider value={state}>{children}</ExtensionStateContext.Provider>;
};
